import { Injectable, Injector, ModelSignal, inject } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { FormActionTypeEnum, SEPAPaymentRailTypeEnum } from '@finxone-platform/form-action';
import { CountriesCurrenciesService, IconCacheService } from '@finxone-platform/shared/services';
import { InputFormatType, NavigationOptionsType } from '@finxone-platform/shared/sys-config-types';
import {
  CurrencyAmountStateAccessor,
  FieldVerificationService,
  GenericHandleBarModel,
  HandlebarModelService,
  WidgetConfigAccessor,
} from '@finxone-platform/ui-components';
import { Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { ConfigService } from '../services/config-service/config-service.service';
import { AccountState } from '../state/account.state';
import { FormActionState } from '../state/form-submision.state';
import { SavingsAccountState } from '../state/savings-account.state';
import {
  handleEmailVerification,
  handlePhoneVerification,
  verifyEmailPhoneButton,
} from '../utils/form-widget-action-utils/input-widget-verify-button-action.utils';
import { buildHandleBarModel } from '../utils/template/model-builder.utils';

/**
 * Web-frontend implementations of the ui-components DI seams, plus a providers array that
 * registers them (and points the widget config seam at the concrete `ConfigService`). Keeping
 * these in the app lets `libs/ui-components` render without importing the application.
 */
@Injectable({ providedIn: 'root' })
export class HandlebarModelServiceImpl extends HandlebarModelService {
  private readonly store = inject(Store);
  private readonly countriesCurrenciesService = inject(CountriesCurrenciesService);
  private readonly iconCacheService = inject(IconCacheService);

  buildModel(): Observable<GenericHandleBarModel> {
    return buildHandleBarModel(this.store, '', this.countriesCurrenciesService, this.iconCacheService);
  }
}

@Injectable({ providedIn: 'root' })
export class FieldVerificationServiceImpl extends FieldVerificationService {
  handleEmailVerification(
    injector: Injector,
    destroy$: Subject<void>,
    showEmailVerifyButton: ModelSignal<boolean>,
    widgetParentFormId: string,
    formGroup: FormGroup,
    formControlName: string,
  ): void {
    handleEmailVerification(
      injector,
      destroy$,
      showEmailVerifyButton,
      widgetParentFormId,
      formGroup,
      formControlName,
    );
  }

  handlePhoneVerification(
    injector: Injector,
    destroy$: Subject<void>,
    showPhoneVerifyButton: ModelSignal<boolean>,
    widgetParentFormId: string,
    formGroup: FormGroup,
    formControlName: string,
    countryCodeKey: string,
    attachToInput: boolean,
  ): void {
    handlePhoneVerification(
      injector,
      destroy$,
      showPhoneVerifyButton,
      widgetParentFormId,
      formGroup,
      formControlName,
      countryCodeKey,
      attachToInput,
    );
  }

  verifyEmailPhoneButton(
    injector: Injector,
    type: InputFormatType | undefined,
    widgetNavigationOptions: NavigationOptionsType,
    formControlName: string,
    parentFormID: string,
    nestedFormId: string,
    phoneNumberAttachToInput: boolean,
    countryCodeFormControl: AbstractControl<any, any> | null | undefined,
    mainFormControl: AbstractControl<any, any> | null | undefined,
    countryCodeFormControlName?: string | undefined,
  ): void {
    verifyEmailPhoneButton(
      injector,
      type,
      widgetNavigationOptions,
      formControlName,
      parentFormID,
      nestedFormId,
      phoneNumberAttachToInput,
      countryCodeFormControl,
      mainFormControl,
      countryCodeFormControlName,
    );
  }
}

@Injectable({ providedIn: 'root' })
export class CurrencyAmountStateAccessorImpl extends CurrencyAmountStateAccessor {
  private readonly store = inject(Store);

  getCurrentAccount() {
    return this.store.select(AccountState.getCurrentAccount);
  }

  getCurrentSavingsAccount() {
    return this.store.select(SavingsAccountState.getCurrentAccount);
  }

  getSepaPaymentRail(): SEPAPaymentRailTypeEnum | undefined {
    return this.store.selectSnapshot(FormActionState.getFormActionStateWithId(FormActionTypeEnum.SEPA_PAYMENT))
      ?.formData?.paymentRail as SEPAPaymentRailTypeEnum | undefined;
  }
}

/**
 * Wires the ui-components DI seams to their web-frontend implementations. Registered in the
 * root providers of every app that renders ui-components widgets.
 */
export const UI_COMPONENT_ADAPTER_PROVIDERS = [
  { provide: WidgetConfigAccessor, useExisting: ConfigService },
  { provide: HandlebarModelService, useClass: HandlebarModelServiceImpl },
  { provide: FieldVerificationService, useClass: FieldVerificationServiceImpl },
  { provide: CurrencyAmountStateAccessor, useClass: CurrencyAmountStateAccessorImpl },
];
